import { Collection, ComponentPrototype, exportDataToFile, isTrue } from '../../shared';
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core';
import { GlobalErrorHandlerService } from '../../services';
import { CLASS_NAME, ERROR_PRIORITY, ERROR_PRIORITY_LABELS, ERROR_TYPE, FEATURE_FLAG } from '../../shared/enum';
import { delay, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { IGlobalError } from '../../shared/types';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { DatePipe } from '@angular/common';
import { IObservableElementProps } from '../../directives';
import { FeatureFlagApiService } from '../../services/feature-flag-api.service';

const MODAL_OPTIONS: NgbModalOptions = {
  backdrop: 'static',
  keyboard: false,
  container: 'body',
  windowClass: 'fade in global-error-handler__modal-window',
  size: 'xl',
  backdropClass: 'fade in',
};

const DEFAULT_LIMIT = 3;

interface IGEFilter {
  type: ERROR_PRIORITY;
  label: string;
  icon: string;
}

@Component({
  selector: 'app-global-error-handler',
  template: `
    <ng-container *ngIf="featureFlagApiService.getFlag(FEATURE_FLAG.HANDLE_ERROR_MESSAGES) | async">
      <ng-container *ngIf="errors$.onChange() | async as errors">
        <div class="global-error-handler">
          <div
            class="global-error-handler__trigger"
            [class.is-enabled]="!!errors.length"
            (click)="errors.length && openModal()"
          >
            <i class="fa fa-bell fa-xlg global-error-handler__icon"></i>
            <i class="fa fa-exclamation-circle global-error-handler__warning-icon {{ priority$ | async }}"></i>
          </div>

          <div class="global-error-handler__items">
            <ng-container *ngFor="let error of displayed$ | async">
              <div class="global-error-handler__item">
                <div class="global-error-handler__item-header">
                  <div>
                    <i class="fa fa-exclamation-triangle"></i>
                    <span>&nbsp;Error ({{ error.status }})</span>
                  </div>
                  <div class="global-error-handler__item-close" (click)="errors$.deselect(error)">
                    <i class="fa fa-times"></i>
                  </div>
                </div>

                <div class="global-error-handler__item-body">
                  <div>{{ error.message }}</div>
                  <div class="global-error-handler__item-link" (click)="openModal()">
                    <b>Click to see details&nbsp;</b>
                    <i class="fa fa-external-link"></i>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>
        </div>

        <ng-template #modal>
          <div class="global-error-handler__modal">
            <div class="global-error-handler__modal-header">
              <div class="global-error-handler__modal-header-title">
                <b>Errors and Notifications</b>
              </div>
              <div class="global-error-handler__modal-close" (click)="closeModal()">
                <i class="fa fa-times"></i>
              </div>
            </div>

            <div class="global-error-handler__modal-content">
              <div class="global-error-handler__grid">
                <div class="global-error-handler__filters">
                  <ng-container *ngFor="let filter of filters$.onChange() | async">
                    <ng-container *ngIf="filters$.hasClass(CLASS_NAME.IS_ACTIVE, filter.type)">
                      <div
                        class="global-error-handler__filter {{ filter.type }}"
                        [class.is-active]="filters$.hasClass(CLASS_NAME.IS_ACTIVE, filter.type)"
                        [class.is-selected]="filters$.isSelected(filter)"
                        (click)="onFilterToggle(filter)"
                      >
                        <i class="fa {{ filter.icon }}"></i>
                        <span>&nbsp;{{ filter.label }}</span>
                      </div>
                    </ng-container>
                  </ng-container>
                </div>
                <ng-container *ngIf="containerSize$ | async as containerSize">
                  <div class="global-error-handler__grid-header">
                    <div class="global-error-handler__grid-header-cells" [style.width]="containerSize.width + 'px'">
                      <ng-container *ngFor="let column of columns">
                        <div class="global-error-handler__grid-header-cell" [style.width]="column.width">
                          <b>{{ column.headerName }}</b>
                        </div>
                      </ng-container>
                    </div>
                  </div>
                </ng-container>
                <div class="global-error-handler__grid-body">
                  <div
                    class="global-error-handler__grid-rows"
                    [appObservableElement]="'width'"
                    [observable$]="containerSize$"
                  >
                    <ng-container *ngIf="errors$.onChange() | async as errors">
                      <ng-container *ngFor="let row of errors.reverse()">
                        <ng-container *ngIf="filters$.isSelected(row.priority)">
                          <div class="global-error-handler__grid-row">
                            <ng-container *ngFor="let column of columns">
                              <div class="global-error-handler__grid-row-cell" [style.width]="column.width">
                                <ng-container *ngIf="column.field === 'priority'">
                                  <span>{{ ERROR_PRIORITY_LABELS[row.priority] }}</span>
                                  <br />
                                  <small>{{ row.timestamp | date : 'dd MM yyyy hh:mm:ss' }}</small>
                                </ng-container>

                                <ng-container *ngIf="column.field === 'causedBy'">
                                  <ng-container *ngIf="row.type === ERROR_TYPE.API_ERROR">
                                    <b>{{ row.origin?.method }}</b>
                                    <br />
                                    <i>{{ row.origin?.urlWithParams }}</i>
                                  </ng-container>
                                </ng-container>

                                <ng-container *ngIf="column.field === 'context'">
                                  <ng-container *ngIf="row.type === ERROR_TYPE.API_ERROR">
                                    <b>{{ row.status }}&nbsp;</b>
                                    <span>{{ row.statusText }}</span>
                                  </ng-container>
                                </ng-container>

                                <ng-container *ngIf="column.field === 'message'">
                                  <span>{{ row.message }}</span>
                                </ng-container>
                              </div>
                            </ng-container>
                          </div>
                        </ng-container>
                      </ng-container>
                    </ng-container>
                  </div>
                </div>
              </div>
            </div>

            <div class="global-error-handler__modal-footer">
              <button type="button" class="btn btn-default" (click)="closeModal()">
                <span>Close</span>
              </button>
              <span>&nbsp;&nbsp;</span>
              <button type="button" class="btn btn-info" (click)="exportToCSV()">
                <span>Export to CSV</span>
              </button>
              <ng-container *ngIf="filters$.hasClass(CLASS_NAME.IS_ACTIVE, ERROR_PRIORITY.CRITICAL)">
                <span>&nbsp;&nbsp;</span>
                <button
                  type="button"
                  class="btn btn-danger"
                  ngbTooltip="Reloading page is required due to critical errors!"
                  tooltipClass="fade in tooltip-danger"
                  container="body"
                  placement="top"
                  (click)="reloadPage()"
                >
                  <span>Reload Page</span>
                </button>
              </ng-container>
            </div>
          </div>
        </ng-template>
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./global-error-handler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalErrorHandlerComponent extends ComponentPrototype implements OnInit {
  @Input() public limit: number = DEFAULT_LIMIT;

  @ViewChild('modal') public modal: HTMLElement;

  public readonly ERROR_TYPE: typeof ERROR_TYPE = ERROR_TYPE;

  public readonly ERROR_PRIORITY: typeof ERROR_PRIORITY = ERROR_PRIORITY;

  public readonly CLASS_NAME: typeof CLASS_NAME = CLASS_NAME;

  public readonly FEATURE_FLAG: typeof FEATURE_FLAG = FEATURE_FLAG;

  public readonly ERROR_PRIORITY_LABELS: any = ERROR_PRIORITY_LABELS;

  public readonly containerSize$: BehaviorSubject<IObservableElementProps> =
    new BehaviorSubject<IObservableElementProps>({});

  public readonly filters$: Collection<IGEFilter> = new Collection<IGEFilter>({
    indexBy: 'type',
    allowMultiselect: true,
  }).add([
    {
      type: ERROR_PRIORITY.ANY,
      label: ERROR_PRIORITY_LABELS[ERROR_PRIORITY.ANY],
      icon: 'fa-exclamation-triangle',
    },
    {
      type: ERROR_PRIORITY.CRITICAL,
      label: ERROR_PRIORITY_LABELS[ERROR_PRIORITY.CRITICAL],
      icon: 'fa-times-circle',
    },
    {
      type: ERROR_PRIORITY.MODERATE,
      label: ERROR_PRIORITY_LABELS[ERROR_PRIORITY.MODERATE],
      icon: 'fa-exclamation-circle',
    },
    {
      type: ERROR_PRIORITY.WARNING,
      label: ERROR_PRIORITY_LABELS[ERROR_PRIORITY.WARNING],
      icon: 'fa-circle',
    },
  ]);

  public readonly errors$: Collection<IGlobalError> = new Collection<IGlobalError>({
    indexBy: 'id',
    allowMultiselect: true,
  });

  public readonly displayed$: Observable<IGlobalError[]> = this.errors$.onSelect().pipe(
    map((errors: IGlobalError[]) => {
      return errors.filter(({ priority }) => priority !== ERROR_PRIORITY.WARNING).slice(0, this.limit);
    })
  );

  public readonly priority$: Observable<string> = this.errors$.onSelect().pipe(
    map((errors: IGlobalError[]) => {
      const len = errors.length;
      let priority = '';

      for (let i = 0; i < len; ) {
        const { WARNING, MODERATE, CRITICAL } = ERROR_PRIORITY;
        const p = errors[i].priority;

        priority = !priority || priority === WARNING || (priority === MODERATE && p === CRITICAL) ? p : priority;
        i += priority === CRITICAL ? len : 1;
      }

      return priority;
    })
  );

  public readonly columns: any = [
    {
      field: 'priority',
      headerName: 'Priority',
      width: '10%',
    },
    {
      field: 'causedBy',
      headerName: 'API Call',
      width: '30%',
    },
    {
      field: 'context',
      headerName: 'Context',
      width: '30%',
    },
    {
      field: 'message',
      headerName: 'Error Msg',
      width: '30%',
    },
  ].map(d => ({ ...d, suppressHeaderMenuButton: true, sortable: false }));

  public handleError = (data: IGlobalError): void => {
    this.errors$.add(data).select(data);
  };

  public openModal = (): void => {
    const { modal, modalService, errors$, filters$ } = this;

    errors$.deselectAll();

    if (errors$.items.length) {
      filters$.select(filters$.filter(f => filters$.hasClass(CLASS_NAME.IS_ACTIVE, f.type)));
      modalService.open(modal, MODAL_OPTIONS);
    }
  };

  public closeModal = (): void => {
    const { modalService } = this;
    modalService.dismissAll();
  };

  public exportToCSV = (): void => {
    const { columns, errors$, datePipe } = this;

    exportDataToFile(
      [
        columns.map(({ headerName }) => headerName).join(','),
        ...errors$.map((row: IGlobalError) => {
          return [
            `"${row.priority} ${datePipe.transform(row.timestamp, 'dd MM yyyy hh:mm:ss')}"`,
            `"${row.origin?.method} ${row.origin?.urlWithParams}"`,
            `"${row.status} ${row.statusText}"`,
            `"${row.message}"`,
          ].join(',');
        }),
      ].join('\n'),
      'Sync Licensing App Errors.csv'
    );
  };

  public reloadPage = (): void => {
    window.location.reload();
  };

  public onFilterToggle = ({ type }: IGEFilter): void => {
    const { filters$ } = this;
    const { ANY } = ERROR_PRIORITY;
    const { IS_ACTIVE } = CLASS_NAME;

    if (filters$.hasClass(IS_ACTIVE, type)) {
      const isSelected = filters$.isSelected(type);
      const selectable = filters$
        .filter((f: IGEFilter) => f.type !== ANY && filters$.hasClass(IS_ACTIVE, f.type))
        .map((f: IGEFilter) => f.type);

      type === ANY
        ? !isSelected && filters$.select([...selectable, ANY])
        : (!isSelected || (isSelected && selectable.length > 1)) && filters$.toggle(type);
    }
  };

  public refreshFiltersActivity = () => {
    const { errors$, filters$ } = this;
    const { ANY, WARNING, CRITICAL, MODERATE } = ERROR_PRIORITY;
    const classNames = {
      [WARNING]: false,
      [MODERATE]: false,
      [CRITICAL]: false,
    };

    errors$.forEach(({ priority }: IGlobalError) => (classNames[priority] = true));

    Object.keys(classNames).forEach(key => filters$.toggleClass(CLASS_NAME.IS_ACTIVE, key, classNames[key]));

    filters$.toggleClass(CLASS_NAME.IS_ACTIVE, ANY, Object.values(classNames).some(isTrue));
  };

  constructor(
    private readonly service: GlobalErrorHandlerService,
    private readonly modalService: NgbModal,
    private readonly datePipe: DatePipe,
    public readonly featureFlagApiService: FeatureFlagApiService
  ) {
    super();
  }

  ngOnInit(): void {
    const { filters$, errors$, service, registerSubscriptions, handleError, refreshFiltersActivity } = this;
    const { ANY, WARNING, MODERATE, CRITICAL } = ERROR_PRIORITY;
    const { IS_ACTIVE } = CLASS_NAME;

    registerSubscriptions(
      ...[WARNING, MODERATE, CRITICAL].map(priority => service.onError(priority).pipe(tap(handleError)).subscribe()),

      errors$.onChange().pipe(tap(refreshFiltersActivity)).subscribe(),

      filters$
        .onSelect()
        .pipe(
          map(() => {
            return filters$
              .filter((f: IGEFilter) => f.type !== ANY && filters$.hasClass(IS_ACTIVE, f.type))
              .every((f: IGEFilter) => filters$.isSelected(f));
          }),
          filter(allSelected => allSelected !== filters$.isSelected(ERROR_PRIORITY.ANY)),
          tap(allSelected => filters$.toggle(ERROR_PRIORITY.ANY, allSelected))
        )
        .subscribe(),

      filters$
        .onClassChange(IS_ACTIVE)
        .pipe(
          tap(() => {
            const selectable = filters$.filter(
              (f: IGEFilter) => f.type !== ANY && filters$.hasClass(IS_ACTIVE, f.type)
            );
            filters$.isSelected(ANY) && selectable.some(f => !filters$.isSelected(f)) && filters$.select(selectable);
          })
        )
        .subscribe()
    );

    if (service.DEBUG_MODE) {
      const emitFakeError = (): any => {
        return of(null).pipe(
          take(1),
          delay(1000 * Math.floor(Math.random() * 10)),
          tap(() => service.emitFakeError()),
          switchMap(() => (errors$.size < 20 ? emitFakeError() : of(null)))
        );
      };

      registerSubscriptions(emitFakeError().subscribe());
    }
  }
}
